import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import BoldIcon from '@src/svgs/BoldIcon';
import CapitalizeIcon from '@src/svgs/CapitalizationIcon';
import ItalicIcon from '@src/svgs/ItalicIcon';
import {
  Bold,
  DEFAULT_BOLD_VALUES,
  getBoldValuesFromCustomFonts,
  getCustomFontFromPostScriptName,
} from '@src/utility/fonts';
import { scrollIfNotInView } from '@src/utility/general';
import { observer } from 'mobx-react-lite';
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { useOutsideAlerter } from '../../transcript/useClickOutside';

import type { KaraokeConfig } from '@src/services/karaoke/karaokeTypes';

export enum Action {
  bold = 'Bold',
  italic = 'Italic',
  capitalize = 'Capitalize',
  align = 'Align',
}

type Props = {
  selectedActions: Action[];
  selectedFontFamily: string;
  selectedBold: string | null;
  hasStickyBottom: boolean;
  visibleActions?: Action[];
  handleSetBold: (
    bold: string,
    fontWeight: number,
    fontScriptName?: string,
  ) => void;
  handleToggleAction: (
    action: Action,
    propertyName: keyof Pick<KaraokeConfig, 'font_style' | 'text_transform'>,
    normalState: string,
    requiredState: string,
  ) => void;
};

const BoldItalicCapitalize = observer(
  forwardRef<HTMLDivElement, Props>((props, ref) => {
    const {
      selectedActions,
      selectedFontFamily,
      selectedBold,
      hasStickyBottom,
      visibleActions,
      handleSetBold,
      handleToggleAction,
    } = props;

    const videoCreator = useVideoCreatorStore();
    const [openBold, toggleBold] = useState<boolean>(false);
    const boldRef = useRef<HTMLButtonElement>(null);
    const boldDropdownRef = useRef<HTMLDivElement>(null);
    const template = videoCreator.textBrandingService.selectedBrandingTemplate;

    const brandCustomFonts =
      videoCreator.organization?.branding?.customFonts || [];
    const customFont = getCustomFontFromPostScriptName(
      selectedFontFamily,
      brandCustomFonts,
    );

    useOutsideAlerter(boldRef, () => {
      toggleBold(false);
    });

    useEffect(() => {
      if (boldDropdownRef.current && ref) {
        const container = (ref as React.RefObject<HTMLDivElement>)
          .current as Element;

        const stickyContainerHeight = hasStickyBottom ? 100 : 0;

        scrollIfNotInView(
          boldDropdownRef.current,
          container,
          stickyContainerHeight,
        );
      }
    }, [openBold, template, hasStickyBottom, ref]);

    const getActionColor = (action: Action) => {
      if (
        (action === Action.bold &&
          selectedBold &&
          selectedBold !== Bold.normal) ||
        selectedActions.includes(action)
      )
        return 'var(--background)';
      return 'var(--text-muted)';
    };

    const renderAction = (action: Action) => {
      switch (action) {
        case Action.bold: {
          const { boldValues: customBoldValues } = getBoldValuesFromCustomFonts(
            selectedFontFamily,
            brandCustomFonts,
          );
          const isBoldSelected =
            !!openBold ||
            customBoldValues.length > 1 ||
            (!customFont && !!selectedBold && selectedBold !== Bold.normal);
          const boldValues = customFont
            ? customBoldValues
            : DEFAULT_BOLD_VALUES;
          return (
            <BoldAction
              ref={boldRef}
              isSelected={isBoldSelected}
              onClick={() => {
                toggleBold(!openBold);
              }}
            >
              <BoldIcon
                fillColor={
                  isBoldSelected ? 'var(--background)' : 'var(--text-muted)'
                }
              />
              {openBold && (
                <Dropdown ref={boldDropdownRef}>
                  {boldValues.map(boldValue => (
                    <DropdownItem
                      key={boldValue.fontSubfamily}
                      isSelected={selectedBold === boldValue.fontSubfamily}
                      boldValue={boldValue.fontWeight}
                      onClick={e => {
                        e.stopPropagation();
                        if (boldValues.length > 1) {
                          handleSetBold(
                            boldValue.fontSubfamily,
                            boldValue.fontWeight,
                            boldValue.fontScriptName,
                          );
                        }
                        toggleBold(false);
                      }}
                    >
                      {boldValue.fontSubfamily}
                    </DropdownItem>
                  ))}
                </Dropdown>
              )}
            </BoldAction>
          );
        }
        case Action.italic:
          return (
            <ActionButton
              key={Action.italic}
              isSelected={selectedActions.includes(Action.italic)}
              onClick={() =>
                handleToggleAction(
                  Action.italic,
                  'font_style',
                  'normal',
                  'italic',
                )
              }
            >
              <ItalicIcon fillColor={getActionColor(Action.italic)} />
            </ActionButton>
          );

        case Action.capitalize:
          return (
            <ActionButton
              key={Action.capitalize}
              isSelected={selectedActions.includes(Action.capitalize)}
              onClick={() =>
                handleToggleAction(
                  Action.capitalize,
                  'text_transform',
                  'none',
                  'uppercase',
                )
              }
            >
              <CapitalizeIcon fillColor={getActionColor(Action.capitalize)} />
            </ActionButton>
          );

        default:
          return null;
      }
    };

    const actions = [Action.bold, Action.italic, Action.capitalize].filter(
      a => !visibleActions || visibleActions.includes(a),
    );

    return <>{actions.map(action => renderAction(action))}</>;
  }),
);

export default BoldItalicCapitalize;

const ActionButton = styled.button<{ isSelected: boolean }>`
  outline: 0;
  border: 1px solid
    ${props => (props.isSelected ? 'var(--accent)' : 'var(--border)')};
  height: 42px;
  width: 100%;
  background-color: ${props =>
    props.isSelected ? 'var(--accent)' : 'var(--background)'};
  border-radius: var(--radius);
  color: ${props => (props.isSelected ? 'var(--background)' : 'var(--border)')};
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  span.filler {
    font-weight: var(--font-weight-normal);
    font-size: 11px;
    line-height: 9.68px;
    color: ${props =>
      props.isSelected ? 'var(--background)' : 'var(--foreground)'};
  }
`;

const BoldAction = styled(ActionButton)`
  position: relative;
`;

const Dropdown = styled.div<{ width?: string }>`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background-color: var(--background);
  box-shadow: var(--elevation-toast);
  text-align: left;
  width: 100px;
  max-height: 200px;
  overflow: auto;
  position: absolute;
  top: 40px;
  left: 0;
  cursor: pointer;
  z-index: 3;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: transparent;
  }
`;

const DropdownItem = styled.span<{ isSelected: boolean; boldValue: number }>`
  padding: 12px;
  background-color: ${props => props.isSelected && 'var(--accent)'};
  &:not(:last-child) {
    border-bottom: 1px solid var(--border);
  }
  font-weight: ${props => props.isSelected && props.boldValue};
  color: ${props =>
    props.isSelected ? 'var(--background)' : 'var(--text-secondary)'};
  &:hover {
    color: ${props =>
      props.isSelected ? 'var(--background)' : 'var(--accent)'};
    font-weight: ${props => props.boldValue};
  }
`;
